import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Button,
  Alert,
  Box,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Logout,
  FactCheckRounded,
  Face,
  PeopleAlt,
  InfoOutlined,
  Dashboard,
  SchoolOutlined,
} from '@mui/icons-material';
import { TEXT_SCALES } from '../../../stores/data/ui.store';
import SiddurUnitsControl from '../siddur/SiddurUnitsControl';
import { restartOnboardingTours } from '../../../hooks/use-onboarding-tour.hooks';
import BurgerMenu from '../../../assets/icons/iconBurgerMenu.svg';
import StarIcon from '../../../assets/SVGComponents/iconStar';
import AppLogo from '../../../assets/icons/iconShanenLi.svg';
import IconMishnaSdura from '../../../assets/SVGComponents/iconMishnaSdura';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import { animate } from 'animejs';
import { Divider } from '@mui/material';
import { getAvatarById } from '../../../utils/avatar.utils';
import LanguageToggle from './LanguageToggle';
import ProfileModal from '../modals/ProfileModal';
import avatarDefault from '../../../assets/images/avatars/avatar_default.png';
import BreadCrumbs from './breadcrumbs';
import LogoutModal from '../modals/LogoutModal';
import MessageModal from '../modals/MessageModal';
import { ROLES } from '@shared/types';

const Header = observer(() => {
  const { t } = useTranslation();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'burgerMenu',
  });
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const userStore = useStoreByName('userStore');

  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [openGuestMessageModal, setOpenGuestMessageModal] = useState(false);
  const [currentAvatarId, setCurrentAvatarId] = useState<number | null>(null);
  // תמונת האווטר לא נטענה (קובץ ישן בקאש, רשת גרועה). בלי זה MUI מציג את
  // האות הראשונה של ה-alt במקום התמונה, וזה נראה כמו תקלה בפרופיל של המשתמש.
  const [hasAvatarImageFailed, setHasAvatarImageFailed] = useState(false);

  // Sync avatar id from user data
  useEffect(() => {
    const avatarIdFromUser = userStore.userData?.personalInfo?.avatar_id;
    if (typeof avatarIdFromUser === 'number' && avatarIdFromUser > 0) {
      setCurrentAvatarId(avatarIdFromUser);
    } else {
      setCurrentAvatarId(null);
    }
    setHasAvatarImageFailed(false);
  }, [userStore.userData?.personalInfo?.avatar_id]);

  const uiStore = useStoreByName('uiStore');
  const rank = userStore.userData?.personalInfo?.rank ?? 0;
  const settings = ['profile', 'personalArea', 'classesManagement', 'adminPanel', 'principalPanel', 'logout'];
  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isTeacher = userRoleId === ROLES.TEACHER;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;
  const GUEST_USER_ID = 544;
  const isGuestUser = userStore.userData?.personalInfo?.id === GUEST_USER_ID;

  const [animatedRank, setAnimatedRank] = useState(rank);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    userStore.userData = null;
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
  };

  const handleMenuItemClick = (setting: string) => {
    if (setting === 'logout') {
      setOpenLogoutDialog(true);
    }
    if (setting === 'personalArea') {
      // Check if user is guest - show message instead of navigating
      if (isGuestUser) {
        setOpenGuestMessageModal(true);
      } else {
        navigate('/personal-area');
      }
    }
    if (setting === 'classesManagement') {
      navigate('/classes-management');
    }
    if (setting === 'adminPanel') {
      // מנהלי מערכת ומנהלי בתי ספר נווטים לפאנל האדמין
      navigate('/admin-panel');
    }
    if (setting === 'principalPanel') {
      navigate('/admin-panel');
    }
    if (setting === 'profile') {
      setOpenProfileModal(true);
    }
    handleCloseUserMenu();
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleConfirmLogout = () => {
    handleLogout();
    setOpenLogoutDialog(false);
  };

  const handleCloseProfileModal = () => {
    setOpenProfileModal(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setLastScrollTop(scrollTop <= 0 ? 0 : scrollTop);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  useEffect(() => {
    const obj = { value: animatedRank };
    const animation = animate(obj, {
      value: rank,
      round: 1,
      ease: 'linear',
      duration: 1000,
      onUpdate: function () {
        setAnimatedRank(Math.round(obj.value));
      },
    });

    return () => {
      animation.pause();
    };
  }, [rank, animatedRank]);

  return (
    <>
      {isGuestUser && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            backgroundColor: '#fff3cd',
            padding: '8px 16px',
            textAlign: 'center',
            zIndex: 1000,
            boxShadow: '0 20px 20px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#856404',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('guestUserIndicator')}
          </Typography>
        </Box>
      )}
      <header className={`header`}>
        <div className="column">
          <Button variant="text" aria-label="תפריט ראשי" {...bindTrigger(popupState)}>
            <img src={BurgerMenu} alt="תפריט" />
          </Button>
          <Menu
            {...bindMenu(popupState)}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  // בטלפון התפריט לא אמור לחרוג מרוחב המסך
                  maxWidth: 'calc(100vw - 32px)',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 28,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                popupState.close();
                // מפעיל מחדש את הסיור על הדף הנוכחי. אם המשתמש נמצא בדף אחר,
                // הסימון מתאפס ממילא והסיור יופיע כשהוא יגיע לדף הבית.
                restartOnboardingTours();
                if (window.location.pathname !== '/home') {
                  navigate('/home');
                }
              }}
            >
              <SchoolOutlined sx={{ fontSize: 34, color: '#5898ff', marginRight: 1 }} />
              {t('showTourAgain')}
            </MenuItem>
            <MenuItem
              sx={{ whiteSpace: 'normal' }}
              onClick={() => {
                uiStore.setShowByHeartOption(!uiStore.showByHeartOption);
              }}
            >
              <Checkbox
                checked={uiStore.showByHeartOption}
                sx={{ marginRight: 1 }}
              />
              {t('showByHeartButton')}
            </MenuItem>
            <Divider />
            {/* גודל הטקסט בקריאה: בחירה של המשתמש, נשמרת במכשיר */}
            <Box sx={{ padding: '8px 16px' }}>
              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {t('textSizeSetting')}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={uiStore.textScale}
                onChange={(_event, scale: number | null) => {
                  if (scale !== null) uiStore.setTextScale(scale);
                }}
                aria-label={t('textSizeSetting')}
              >
                {TEXT_SCALES.map((scale, index) => (
                  <ToggleButton key={scale} value={scale} sx={{ textTransform: 'none' }}>
                    {t(['textSizeNormal', 'textSizeLarge', 'textSizeLarger'][index])}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            {/* יחידות במסך: רק בסידור, שם זה אומר משהו */}
            {window.location.pathname.startsWith('/siddur') && (
              <Box sx={{ padding: '8px 16px' }} onClick={(event) => event.stopPropagation()}>
                <SiddurUnitsControl />
              </Box>
            )}
            <Divider />
            {/* זרימה רציפה: בסיום פסוק ממשיכים לבד, עם מיקרופון שנשאר פתוח */}
            <MenuItem
              sx={{ whiteSpace: 'normal' }}
              onClick={() => {
                uiStore.setAutoFlowEnabled(!uiStore.autoFlowEnabled);
              }}
            >
              <Checkbox checked={uiStore.autoFlowEnabled} sx={{ marginRight: 1 }} />
              {t('autoFlowSetting')}
            </MenuItem>
            <Box sx={{ padding: '0 16px 8px' }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', maxWidth: 260 }}>
                {t('autoFlowSettingHint')}
              </Typography>
            </Box>
            {uiStore.autoFlowEnabled && (
              <Box sx={{ padding: '4px 16px 12px' }} onClick={(event) => event.stopPropagation()}>
                <Typography variant="caption" sx={{ display: 'block', marginBottom: '6px' }}>
                  {t('autoFlowReadsPerVerse')}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={uiStore.readsPerVerse}
                  onChange={(_event, value) => {
                    if (value) uiStore.setReadsPerVerse(Number(value));
                  }}
                >
                  {[1, 2, 3, 4].map((times) => (
                    <ToggleButton key={times} value={times} sx={{ minWidth: 44 }}>
                      {times}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}
            <Divider />
            <MenuItem
              onClick={() => {
                window.open('https://mishnasdura.org.il/', '_blank');
                popupState.close();
              }}
            >
              <IconMishnaSdura sx={{ fontSize: 34, color: '#5898ff', marginRight: 1 }} />
              {t('mishnaSeduraWebsite')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                window.open('https://mishnasdura.org.il/about/', '_blank');
                popupState.close();
              }}
            >
              <InfoOutlined sx={{ fontSize: 34, color: '#5898ff', marginRight: 1 }} />
              {t('aboutUs')}
            </MenuItem>
          </Menu>
          <div className="brand-container">
            <Tooltip title={t('homePage')}>
              <IconButton onClick={() => navigate('/home')}>
                <img src={AppLogo} alt={t('appName')} />
              </IconButton>
            </Tooltip>
            <h3>{t('appName')}</h3>
            <p>{t('appDescription')}</p>
          </div>
        </div>

        <div className="column">
          {/* {isTeacher && (
            <Tooltip title={t('classesManagement')}>
              <IconButton className="classes-management-button" onClick={() => navigate('/classes-management')}>
                <img src={ClassIcon} alt="teacher-icon" />
              </IconButton>
            </Tooltip>
          )} */}

          <LanguageToggle />

          <div className="score-tag">
            <StarIcon strokeColor="trasparent" />
            <h3>{animatedRank}</h3>
          </div>

          <Tooltip title={t('openSettings')}>
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
              <Avatar
                alt="תמונת פרופיל"
                src={(() => {
                  // הטעינה נכשלה - בלי src, וכך MUI מציג את אייקון המשתמש הכללי
                  if (hasAvatarImageFailed) {
                    return undefined;
                  }

                  // אם אין מזהה אווטר, השתמש בדיפולטיבי
                  if (!currentAvatarId) {
                    return avatarDefault;
                  }

                  // אם יש מזהה, נסה לקבל את התמונה
                  const avatarSrc = getAvatarById(
                    currentAvatarId,
                    isTeacher ? 'teacher' : isAdmin ? 'admin' : isPrincipal ? 'admin' : 'student'
                  );

                  // אם התמונה תקינה, השתמש בה, אחרת השתמש בדיפולטיבית
                  return typeof avatarSrc === 'string' && avatarSrc.length > 0 ? avatarSrc : avatarDefault;
                })()}
                imgProps={{ onError: () => setHasAvatarImageFailed(true) }}
                sx={{ height: 60, width: 60 }}
              />
            </IconButton>
          </Tooltip>
          <Menu
            sx={{ top: '56px' }}
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 40,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              },
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {settings
              .filter((setting: string) => {
                // רק מורים יכולים לראות ניהול כיתות
                if (setting === 'classesManagement' && userRoleId !== ROLES.TEACHER) {
                  return false;
                }
                // רק מנהלים ומנהלי בתי ספר יכולים לראות פאנל ניהול
                if (setting === 'adminPanel' && !isAdmin && !isPrincipal) {
                  return false;
                }
                // הסתרת פאנל ניהול בתי ספר - הוא יופיע תחת "פאנל ניהול"
                if (setting === 'principalPanel') {
                  return false;
                }
                return true;
              })
              .map((setting: string, index: number, filteredArray: string[]) => [
                <MenuItem key={setting} onClick={() => handleMenuItemClick(setting)}>
                  {setting === 'profile' && <Face sx={{ color: '#5898ff' }} />}
                  {setting === 'personalArea' && <FactCheckRounded sx={{ color: '#5898ff' }} />}
                  {setting === 'classesManagement' && <PeopleAlt sx={{ color: '#5898ff' }} />}
                  {setting === 'adminPanel' && <Dashboard sx={{ color: '#5898ff' }} />}
                  {setting === 'logout' && <Logout sx={{ color: '#5898ff' }} />}
                  <Typography textAlign="center" sx={{ ml: 1 }}>
                    {t(setting)}
                  </Typography>
                </MenuItem>,
                index < filteredArray.length - 1 && <Divider key={`divider-${setting}`} />,
              ])
              .flat()
              .filter(Boolean)}
          </Menu>
        </div>
      </header>

      <BreadCrumbs />

      <LogoutModal isOpen={openLogoutDialog} onClose={handleCloseLogoutDialog} onConfirm={handleConfirmLogout} />

      <ProfileModal isOpen={openProfileModal} onClose={handleCloseProfileModal} />

      <MessageModal
        isOpen={openGuestMessageModal}
        onClose={() => setOpenGuestMessageModal(false)}
        title={t('guestUserPersonalAreaTitle')}
        message={t('guestUserPersonalAreaMessage')}
        type="info"
      />
    </>
  );
});

export default Header;
